import type { RichtextStoryblok } from "~/types/storyblok";

/**
 * Determines whether a given rich text object contains any non-empty text content.
 *
 * This function recursively traverses the nodes of a rich text structure (such as those
 * returned by Storyblok) to check if there is at least one node of type "text" with
 * non-whitespace content. It also handles nested content within nodes like paragraphs,
 * headings, or list items.
 *
 * @param richText - The rich text object to check, expected to have a `content` array.
 * @returns `true` if there is at least one non-empty text node; otherwise, `false`.
 */
export function hasRichTextContent(richText: RichtextStoryblok) {
  if (!richText || !richText.content || !Array.isArray(richText.content) || richText.content.length === 0) {
    return false;
  }

  // Inner recursive function to check nodes for actual text content
  const checkNodesForText = (nodes: RichtextStoryblok[]) => {
    if (!nodes || !Array.isArray(nodes)) {
      return false;
    }
    for (const node of nodes) {
      // Check for text in a 'text' node
      if (node.type === "text" && node.text && node.text.trim() !== "") {
        return true; // Found actual text
      }
      // Recursively check 'content' of other node types (e.g., paragraph, heading, list_item)
      if (node.content && checkNodesForText(node.content)) {
        return true; // Found text in a nested node
      }
    }
    return false; // No text found in this array of nodes
  };

  return checkNodesForText(richText.content);
}
